import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../config/api";
import "./Changes.css";
import { formatDateTime } from "../utils/dateUtils";
import Select from "react-select";
import { useChangesContext } from "../contexts/ChangesContext";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";
import { darkSelectStyles } from "../utils/selectStyles";

interface CoderChange {
  id: number;
  gameId: number;
  previous_coderId: number | null;
  new_coderId: number | null;
  aia_codage_id: string;
  change_type?: "coder_changed" | "coder_deleted" | "coder_added";
  // Derived by the API from the codage's current state: `is_applied` is true once the
  // change has been acted upon — the codage carries the new coder, or the contract has
  // been cancelled. `metadata.contract_status` cannot answer this: it is a snapshot from
  // when the change was detected. `is_superseded` marks a change a later one on the same
  // codage has overtaken: nothing can apply it any more, and acting on it would undo the
  // assignment that replaced it.
  state?: "pending" | "applied" | "dismissed" | "superseded";
  current_coderId?: number | null;
  current_contract_status?: string | null;
  is_applied?: boolean;
  is_superseded?: boolean;
  change_reason: string;
  change_date: string;
  metadata: any;
  dismissed_at?: string | null;
  sync_detected_at?: string;
  contract_status?: "pending" | "executed" | "verified" | "paid";
  game?: {
    id: number;
    gameName: string;
    aia_game_id: string;
    game_date?: string;
    status?: string;
  };
  previous_coder?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  new_coder?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  entity_type?: "coder"; // Pour distinguer le type
}

interface SupervisorChange {
  id: number;
  gameId: number;
  previous_supervisorId: number | null;
  new_supervisorId: number | null;
  aia_supervision_id: string;
  change_type_name?: "supervisor_changed" | "supervisor_deleted" | "addition";
  // Derived by the API from the supervision's current state: `is_applied` is true once
  // the supervision carries the supervisor this change asks for — whether a click here
  // put it there or a later sync did.
  state?: "pending" | "applied" | "dismissed";
  current_supervisorId?: number | null;
  current_invoice_status?: string | null;
  is_applied?: boolean;
  change_reason: string;
  change_date: string;
  metadata: any;
  notes?: string;
  dismissed_at?: string | null;
  game?: {
    id: number;
    gameName: string;
    aia_game_id: string;
    game_date?: string;
    status?: string;
  };
  previous_supervisor?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  new_supervisor?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  entity_type?: "supervisor"; // Pour distinguer le type
}

type Change = (CoderChange | SupervisorChange) & {
  entity_type: "coder" | "supervisor";
};

const STATE_OPTIONS = [
  { value: "pending" as const, label: "À traiter" },
  { value: "applied" as const, label: "Appliqué" },
  { value: "superseded" as const, label: "Remplacé" },
  { value: "dismissed" as const, label: "Ignoré" },
  { value: "all" as const, label: "Tout" },
];

const STATE_LABELS: Record<string, string> = {
  pending: "● À traiter",
  applied: "✓ Appliqué",
  superseded: "↷ Remplacé",
  dismissed: "⊘ Ignoré",
};

const Changes: React.FC = () => {
  const [changes, setChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "coder" | "supervisor">("all");
  const [dateFilter, setDateFilter] = useState<string>("");
  // Paginated server-side: the two change lists are append-only and were both loaded
  // whole on every visit.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Opens on the work still to do, or on whatever `?state=` asks for — how a dashboard
  // figure links here, so that the number clicked and the rows listed are the same set.
  // Read once, as the initial value: afterwards the dropdown owns it.
  const [searchParams] = useSearchParams();
  const [stateFilter, setStateFilter] = useState<
    "pending" | "applied" | "superseded" | "dismissed" | "all"
  >(() => {
    const requested = searchParams.get("state");
    return requested === "applied" ||
      requested === "superseded" ||
      requested === "dismissed" ||
      requested === "all"
      ? requested
      : "pending";
  });
  const { triggerRefresh } = useChangesContext();

  /**
   * Loads a page of changes from the two endpoints and merges them.
   *
   * Both are ordered the same way — pending before applied, newest first — and both are
   * asked for everything up to the end of the current page. Merging two lists sorted on
   * the same key and taking the window is exactly the window of their union, so the page
   * is correct rather than an approximation of it. The cost is that deep pages fetch more
   * rows; at these volumes that is cheaper than a union view in SQL.
   *
   * The type filter picks which endpoints are asked at all, so filtering to one kind
   * halves the work.
   */
  const fetchChanges = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("page", "1");
      params.append("limit", String(page * pageSize));
      if (dateFilter) params.append("date", dateFilter);
      if (stateFilter !== "all") params.append("state", stateFilter);

      const wantCoders = filter === "all" || filter === "coder";
      const wantSupervisors = filter === "all" || filter === "supervisor";

      const [coderResponse, supervisorResponse] = await Promise.all([
        wantCoders
          ? api.get(`/api/coder-changes?${params.toString()}`)
          : Promise.resolve(null),
        wantSupervisors
          ? api.get(`/api/supervisor-changes?${params.toString()}`)
          : Promise.resolve(null),
      ]);

      const coderChanges: Change[] = (coderResponse?.data.data ?? []).map(
        (c: CoderChange) => ({ ...c, entity_type: "coder" as const }),
      );

      const supervisorChanges: Change[] = (
        supervisorResponse?.data.data ?? []
      ).map((s: SupervisorChange) => ({
        ...s,
        entity_type: "supervisor" as const,
      }));

      // Same order the API applies, so the merge preserves it.
      const allChanges = [...coderChanges, ...supervisorChanges].sort(
        (a, b) => {
          const appliedDifference =
            Number(a.is_applied ?? false) - Number(b.is_applied ?? false);
          if (appliedDifference !== 0) return appliedDifference;

          return (
            new Date(b.change_date).getTime() -
            new Date(a.change_date).getTime()
          );
        },
      );

      const combinedTotal =
        (coderResponse?.data.pagination.total ?? 0) +
        (supervisorResponse?.data.pagination.total ?? 0);

      const combinedTotalPages = Math.max(
        1,
        Math.ceil(combinedTotal / pageSize),
      );

      setTotal(combinedTotal);
      setTotalPages(combinedTotalPages);

      // Handling the last change on a page leaves that page beyond the end of a now
      // shorter list; step back rather than showing an empty page. The effect re-runs on
      // `page`, and this only fires while the page is out of range, so it settles.
      if (page > combinedTotalPages) {
        setPage(combinedTotalPages);
        return;
      }

      setChanges(allChanges.slice((page - 1) * pageSize, page * pageSize));
    } catch (error) {
      console.error("Error fetching changes:", error);
    } finally {
      setLoading(false);
    }
  }, [filter, dateFilter, stateFilter, page, pageSize]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  const getChangeDescription = (change: Change) => {
    if (change.entity_type === "coder") {
      const coderChange = change as CoderChange;
      if (coderChange.change_type === "coder_changed") {
        return `Assignation modifiée : ${coderChange.previous_coder?.first_name} ${coderChange.previous_coder?.last_name} → ${coderChange.new_coder?.first_name} ${coderChange.new_coder?.last_name}`;
      } else if (coderChange.change_type === "coder_deleted") {
        return `Codeur supprimé : ${coderChange.previous_coder?.first_name} ${coderChange.previous_coder?.last_name}`;
      } else if (coderChange.change_type === "coder_added") {
        return `Nouveau codeur assigné : ${coderChange.new_coder?.first_name} ${coderChange.new_coder?.last_name}`;
      }
    } else {
      const supervisorChange = change as SupervisorChange;
      if (supervisorChange.change_type_name === "supervisor_changed") {
        return `Superviseur modifié : ${supervisorChange.previous_supervisor?.first_name} ${supervisorChange.previous_supervisor?.last_name} → ${supervisorChange.new_supervisor?.first_name} ${supervisorChange.new_supervisor?.last_name}`;
      } else if (supervisorChange.change_type_name === "supervisor_deleted") {
        return `Superviseur supprimé : ${supervisorChange.previous_supervisor?.first_name} ${supervisorChange.previous_supervisor?.last_name}`;
      } else if (supervisorChange.change_type_name === "addition") {
        return `Nouveau superviseur assigné : ${supervisorChange.new_supervisor?.first_name} ${supervisorChange.new_supervisor?.last_name}`;
      }
    }
    return "Type de modification inconnu";
  };

  const getContractStatusDescription = (status?: string) => {
    switch (status) {
      case "pending":
        return "En attente d'exécution";
      case "executed":
        return "Service exécuté";
      case "verified":
        return "Service vérifié";
      case "paid":
        return "Paiement effectué";
      default:
        return "Statut inconnu";
    }
  };

  const getContractStatusClass = (status?: string) => {
    switch (status) {
      case "pending":
        return "status-pending";
      case "executed":
        return "status-executed";
      case "verified":
        return "status-verified";
      case "paid":
        return "status-paid";
      default:
        return "status-unknown";
    }
  };

  const getChangeColor = (change: Change) => {
    if (change.entity_type === "coder") {
      const coderChange = change as CoderChange;
      if (coderChange.change_type === "coder_changed") {
        return "change-warning";
      } else if (coderChange.change_type === "coder_deleted") {
        return "change-danger";
      } else if (coderChange.change_type === "coder_added") {
        return "change-success";
      }
    } else {
      const supervisorChange = change as SupervisorChange;
      if (supervisorChange.change_type_name === "supervisor_changed") {
        return "change-warning";
      } else if (supervisorChange.change_type_name === "supervisor_deleted") {
        return "change-danger";
      } else if (supervisorChange.change_type_name === "addition") {
        return "change-success";
      }
    }
    return "change-info";
  };

  const formatDate = (dateString: string) => {
    return formatDateTime(dateString);
  };

  const dismissChange = async (
    changeId: number,
    entityType: "coder" | "supervisor",
  ) => {
    try {
      const endpoint =
        entityType === "coder"
          ? `/api/coder-changes/${changeId}/dismiss`
          : `/api/supervisor-changes/${changeId}/dismiss`;

      await api.patch(endpoint);
      // Reloaded rather than patched in place: the card has just left the "à traiter"
      // filter it was listed under, and the totals and page count move with it.
      await fetchChanges();
      // The badge counts what is still to handle, so it changes too.
      triggerRefresh();
    } catch (error) {
      console.error("Error dismissing change:", error);
    }
  };

  const restoreChange = async (
    changeId: number,
    entityType: "coder" | "supervisor",
  ) => {
    try {
      const endpoint =
        entityType === "coder"
          ? `/api/coder-changes/${changeId}/restore`
          : `/api/supervisor-changes/${changeId}/restore`;

      await api.patch(endpoint);
      // Same reasoning as dismissing: the card no longer belongs under the filter it was
      // listed under.
      await fetchChanges();
      triggerRefresh();
    } catch (error) {
      console.error("Error restoring change:", error);
    }
  };

  const replaceContract = async (changeId: number) => {
    // The new coder has necessarily accepted their coding request: in AIA, a coder only
    // appears on a coding once the request has been accepted.
    const confirmMessage =
      "Êtes-vous sûr de vouloir remplacer le contrat ? Cette action va :\n" +
      "1. Annuler le contrat de l'ancienne entreprise\n" +
      "2. Envoyer un nouveau contrat à l'entreprise du nouveau codeur\n\n" +
      "Cette action est irréversible.";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await api.post(
        `/api/coder-changes/${changeId}/replace-contract`,
      );

      if (response.data.success) {
        const newContract = response.data.new_contract;
        alert(
          `✅ Contrat remplacé avec succès !\n\n` +
            `${
              response.data.old_contract
                ? `Ancien contrat #${response.data.old_contract.id} ${
                    response.data.old_contract.cancelled
                      ? "annulé"
                      : "non annulable (peut-être déjà signé)"
                  }\n`
                : ""
            }` +
            `Nouveau contrat #${newContract.id} envoyé à :\n` +
            `📧 ${newContract.sent_to}\n` +
            `🏢 ${newContract.enterprise_name}\n` +
            `👤 Pour le codeur: ${newContract.coder_name}`,
        );

        // Refresh the changes list
        await fetchChanges();
      }
    } catch (error: any) {
      console.error("Error replacing contract:", error);
      const errorMessage = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du remplacement du contrat :\n${errorMessage}`);
    }
  };

  /**
   * Applies a pending supervisor change: the supervision takes the supervisor AIA
   * reports, and its invoicing goes back to "non facturé" so it is billed again for
   * whoever actually did the work. The sync refuses to move the supervisor of an
   * invoiced supervision on its own, which is why this exists.
   */
  const replaceSupervisor = async (changeId: number, removal: boolean) => {
    const confirmMessage = removal
      ? "Êtes-vous sûr de vouloir retirer le superviseur ? Cette action va :\n" +
        "1. Retirer le superviseur de la supervision\n" +
        "2. Repasser le statut de facturation à « Non facturé »\n\n" +
        "Cette action est irréversible."
      : "Êtes-vous sûr de vouloir remplacer le superviseur ? Cette action va :\n" +
        "1. Affecter le superviseur indiqué par AIA à la supervision\n" +
        "2. Repasser le statut de facturation à « Non facturé »\n\n" +
        "Cette action est irréversible.";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await api.post(
        `/api/supervisor-changes/${changeId}/replace-supervisor`,
      );

      if (response.data.success) {
        const s = response.data.supervision;
        alert(
          `✅ Superviseur ${removal ? "retiré" : "remplacé"} avec succès !\n\n` +
            `Match : ${s.game}\n` +
            `Superviseur : ${s.new_supervisor_name ?? "aucun"}\n` +
            `Facturation : ${s.previous_invoice_status} → ${s.invoice_status}`,
        );

        await fetchChanges();
        triggerRefresh();
      }
    } catch (error: any) {
      console.error("Error replacing supervisor:", error);
      const message = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du remplacement du superviseur :\n${message}`);
    }
  };

  const cancelContract = async (changeId: number) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir annuler le contrat ? Cette action va :\n" +
          "1. Annuler le contrat existant car le codeur a été retiré\n\n" +
          "Cette action est irréversible.",
      )
    ) {
      return;
    }

    try {
      const response = await api.post(
        `/api/coder-changes/${changeId}/cancel-contract`,
      );

      if (response.data.success) {
        alert(
          `✅ Contrat annulé avec succès !\n\n` +
            `${
              response.data.contract
                ? `Contrat #${response.data.contract.id} annulé`
                : "Aucun contrat à annuler"
            }`,
        );

        // Refresh the changes list
        await fetchChanges();
      }
    } catch (error: any) {
      console.error("Error cancelling contract:", error);
      const errorMessage = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors de l'annulation du contrat :\n${errorMessage}`);
    }
  };

  return (
    <div className="coder-changes-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Modifications des Assignations</h1>
        </div>

        <div className="page-content">
          <div className="filters">
            <div className="filter-group">
              <label className="filter-label">Type de Modification</label>
              <Select
                options={[
                  { value: "all", label: "Toutes les Modifications" },
                  { value: "coder", label: "Codeurs" },
                  { value: "supervisor", label: "Superviseurs" },
                ]}
                value={{
                  value: filter,
                  label:
                    filter === "all"
                      ? "Toutes les Modifications"
                      : filter === "coder"
                        ? "Codeurs"
                        : "Superviseurs",
                }}
                onChange={(selectedOption) => {
                  setFilter(selectedOption?.value as any);
                  setPage(1);
                }}
                styles={darkSelectStyles}
                isSearchable={false}
                placeholder="Sélectionner un type..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">État</label>
              <Select
                options={STATE_OPTIONS}
                value={STATE_OPTIONS.find(
                  (option) => option.value === stateFilter,
                )}
                onChange={(selectedOption) => {
                  setStateFilter(selectedOption?.value as any);
                  setPage(1);
                }}
                styles={darkSelectStyles}
                isSearchable={false}
                placeholder="Sélectionner un statut..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Date</label>
              <input
                type="date"
                className="date-input-modern"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="Sélectionner une date..."
              />
            </div>
          </div>

          {loading ? (
            <div className="loading">Chargement des modifications...</div>
          ) : (
            <div className="changes-list">
              {changes.length === 0 ? (
                <div className="no-changes">
                  <p>
                    {stateFilter === "pending"
                      ? "Aucune modification à traiter."
                      : "Aucune modification trouvée pour les critères sélectionnés."}
                  </p>
                </div>
              ) : (
                changes.map((change) => {
                  const isCoderChange = change.entity_type === "coder";
                  const coderChange = change as CoderChange;
                  const supervisorChange = change as SupervisorChange;
                  // The API decides precedence between the states; the card only renders
                  // what it is told. Only a pending change carries actions, so a
                  // superseded one loses them without a rule of its own here.
                  const changeState =
                    (isCoderChange
                      ? coderChange.state
                      : supervisorChange.state) ?? "pending";
                  const isDismissed = changeState === "dismissed";

                  return (
                    <div
                      key={`${change.entity_type}-${change.id}`}
                      className={`change-item ${getChangeColor(change)} ${
                        changeState === "pending" ? "unread" : "read"
                      }`}
                    >
                      <div className="change-header">
                        <div className="change-header-top">
                          <div className="change-main-info">
                            <span
                              className={`entity-badge ${
                                isCoderChange
                                  ? "badge-coder"
                                  : "badge-supervisor"
                              }`}
                            >
                              {isCoderChange ? "CODEUR" : "SUPERVISEUR"}
                            </span>
                            <h3 className="change-description">
                              {getChangeDescription(change)}
                            </h3>
                          </div>
                          <div className="change-header-actions">
                            <span
                              className={`read-status ${
                                changeState === "pending" ? "unread" : "read"
                              }`}
                              title={
                                isDismissed &&
                                (isCoderChange
                                  ? coderChange.dismissed_at
                                  : supervisorChange.dismissed_at)
                                  ? `Ignoré le ${formatDate(
                                      (isCoderChange
                                        ? coderChange.dismissed_at
                                        : supervisorChange.dismissed_at) as string,
                                    )}`
                                  : undefined
                              }
                            >
                              {STATE_LABELS[changeState]}
                            </span>
                            {!isCoderChange &&
                              changeState === "pending" &&
                              supervisorChange.change_type_name ===
                                "supervisor_changed" && (
                                <button
                                  className="btn-replace-contract"
                                  onClick={() =>
                                    replaceSupervisor(change.id, false)
                                  }
                                  title="Affecter le superviseur indiqué par AIA et repasser la facturation à « Non facturé »"
                                >
                                  Remplacer le superviseur
                                </button>
                              )}
                            {!isCoderChange &&
                              changeState === "pending" &&
                              supervisorChange.change_type_name ===
                                "supervisor_deleted" && (
                                <button
                                  className="btn-cancel-contract"
                                  onClick={() =>
                                    replaceSupervisor(change.id, true)
                                  }
                                  title="Retirer le superviseur et repasser la facturation à « Non facturé »"
                                >
                                  Retirer le superviseur
                                </button>
                              )}
                            {isCoderChange && changeState === "pending" && (
                              <>
                                {/* CAS 1: Codeur supprimé - afficher bouton d'annulation */}
                                {coderChange.change_type ===
                                  "coder_deleted" && (
                                  <button
                                    className="btn-cancel-contract"
                                    onClick={() => cancelContract(change.id)}
                                    title="Annuler le contrat car le codeur a été retiré"
                                  >
                                    Annuler le contrat
                                  </button>
                                )}

                                {/* CAS 2: Changement de codeur validé */}
                                {coderChange.change_type === "coder_changed" &&
                                  coderChange.new_coderId && (
                                    <>
                                      {(() => {
                                        const contractStatus =
                                          coderChange.metadata?.contract_status;

                                        // Cas bloquant : contrat déjà annulé
                                        if (contractStatus === "cancelled") {
                                          return (
                                            <div
                                              className="info-badge"
                                              title="Le contrat a déjà été annulé."
                                            >
                                              ℹ️ Contrat annulé
                                            </div>
                                          );
                                        }

                                        // Pour tous les autres cas : afficher le bouton
                                        // (y compris si contract_status n'existe pas dans metadata)
                                        const isSigned =
                                          contractStatus === "signed";
                                        return (
                                          <>
                                            <button
                                              className="btn-replace-contract"
                                              onClick={() =>
                                                replaceContract(change.id)
                                              }
                                              title={
                                                isSigned
                                                  ? "Remplacer le contrat signé de l'ancien codeur par un nouveau contrat pour le nouveau codeur"
                                                  : "Annuler l'ancien contrat et envoyer un nouveau contrat à l'entreprise du nouveau codeur"
                                              }
                                            >
                                              Remplacer le contrat
                                            </button>
                                            {/* Avertissement si contrat déjà signé */}
                                            {isSigned && (
                                              <div
                                                className="warning-badge"
                                                style={{ marginTop: "8px" }}
                                                title="Le contrat de l'ancien codeur est déjà signé. Il sera marqué comme obsolète et un nouveau contrat sera créé pour le nouveau codeur."
                                              >
                                                ⚠️ Contrat déjà signé par ancien
                                                codeur
                                              </div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </>
                                  )}
                              </>
                            )}
                            {isDismissed ? (
                              <button
                                className="btn-mark-unread"
                                onClick={() =>
                                  restoreChange(change.id, change.entity_type)
                                }
                                title="Remettre cette modification parmi celles à traiter"
                              >
                                Remettre à traiter
                              </button>
                            ) : (
                              changeState === "pending" && (
                                <button
                                  className="btn-mark-read"
                                  onClick={() =>
                                    dismissChange(change.id, change.entity_type)
                                  }
                                  title="Aucune action nécessaire sur cette modification"
                                >
                                  Ignorer
                                </button>
                              )
                            )}
                          </div>
                        </div>

                        <div className="change-meta-info">
                          <div className="meta-item">
                            <span className="meta-label">Match</span>
                            <span className="meta-value game-name">
                              {change.game?.gameName || "Match Inconnu"}
                              {change.game?.game_date && (
                                <span className="game-date-inline">
                                  {" · "}
                                  {formatDate(change.game.game_date)}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Détecté le</span>
                            <span className="meta-value">
                              {isCoderChange && coderChange.sync_detected_at
                                ? formatDate(coderChange.sync_detected_at)
                                : formatDate(change.change_date)}
                            </span>
                          </div>
                          {change.game?.status && (
                            <div className="meta-item">
                              <span className="meta-label">
                                Statut du match
                              </span>
                              <span className="meta-value game-status">
                                {change.game.status}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="change-details">
                        <div className="detail-grid">
                          <div className="detail-item">
                            <span className="detail-label">
                              {isCoderChange ? "ID Codage" : "ID Supervision"}
                            </span>
                            <code className="detail-value aia-id">
                              {isCoderChange
                                ? coderChange.aia_codage_id
                                : (change as SupervisorChange)
                                    .aia_supervision_id}
                            </code>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Raison</span>
                            <span className="detail-value change-reason">
                              {change.change_reason}
                            </span>
                          </div>
                          {isCoderChange && coderChange.contract_status && (
                            <div className="detail-item">
                              <span className="detail-label">
                                Statut du contrat
                              </span>
                              <span
                                className={`detail-value contract-status ${getContractStatusClass(
                                  coderChange.contract_status,
                                )}`}
                              >
                                {getContractStatusDescription(
                                  coderChange.contract_status,
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {change.metadata && (
                        <div className="change-metadata">
                          <details>
                            <summary>Détails Techniques</summary>
                            <pre className="metadata-content">
                              {JSON.stringify(change.metadata, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Affichage de {(page - 1) * pageSize + 1} à{" "}
                {Math.min(page * pageSize, total)} sur {total} modifications
              </span>
              <span>
                Éléments par page{" "}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                ««
              </button>
              <button
                className="pagination-btn"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                ‹
              </button>
              <span className="pagination-current">
                Page {page} sur {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={page >= totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
              >
                »»
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Changes;
